import * as React from "react";
import { Stack } from "@mui/material";
import { Account, Document, Person, ServiceProvider } from "@/types";
import { ServiceProviderType } from "@/types/index";
import { Property } from "@/types/property";
import { BankingAccount } from "./BankingAccount";
import { CreditCardsAccount } from "./CreditCardsAccount";
import { DefaultAccount } from "./DefaultAccount";
import { EnergyAccount } from "./EnergyAccount";
import { InsuranceAccount } from "./InsuranceAccount";
import { MobileAccount } from "./MobileAccount";
import { PensionAccount } from "./PensionAccount";
import { WaterAccount } from "./WaterAccount";
import { MortgageAccount } from "./MortgageAccount";
import { SocialMediaOrEmailAccount } from "./SocialMediaOrEmailAccount";
import { NEQuilterAccountSummary, QuilterAccountSummary } from "@/pages/ExpressForm/Submit/InvestmentsAccount";

export type AccountDetailsProps = {
  readonly persons: ReadonlyArray<Person>;
  readonly properties: ReadonlyArray<Property>;
  readonly serviceProviders: ReadonlyArray<ServiceProvider>;
  readonly account: Account;
  readonly uploadedFileInfo: (id: string) => Promise<Document>;
};

export const AccountDetails: React.FC<AccountDetailsProps> = ({
  persons,
  properties,
  serviceProviders,
  account,
  uploadedFileInfo,
}) => {
  const { serviceProviderType } = account;
  const [documents, setDocuments] = React.useState([] as Document[]);

  React.useEffect(() => {
    let unmounted = false;
    Promise.all((account.documentIds || []).map(uploadedFileInfo)).then(
      (documents) => {
        if (unmounted) {
          return;
        }
        setDocuments(documents);
      },
      (err) => {
        console.log(err);
        if (unmounted) {
          return;
        }
        setDocuments([]);
      }
    );
    return () => {
      unmounted = true;
    };
  }, [account.documentIds, setDocuments, uploadedFileInfo]);

  const selectedServiceProvider = React.useMemo(() => {
    return serviceProviders.find((sp) => sp.id === account.companyId);
  }, [serviceProviders, account.companyId]);

  if (!serviceProviderType) {
    return null;
  }
  if (
    account.additionalServicesAccount === true ||
    account.serviceProviderType === "affiliate_partner"
  )
    return null;

  return (
    <AccountDetailsView
      serviceProvider={selectedServiceProvider || ({ customForm: undefined } as ServiceProvider)}
      serviceProviderType={serviceProviderType}
      documents={documents}
      account={account}
      persons={persons}
      properties={properties}
    />
  );
};
export type AccountDetailsViewProps = {
  readonly serviceProvider: ServiceProvider;
  readonly serviceProviderType: ServiceProviderType;
  readonly account: Account;
  readonly persons: ReadonlyArray<Person>;
  readonly properties: ReadonlyArray<Property>;
  readonly documents: ReadonlyArray<Document>;
};

export const AccountDetailsView: React.FC<AccountDetailsViewProps> = ({
  serviceProvider,
  serviceProviderType,
  account,
  persons,
  documents,
  properties,
}) => {
  const accountComponentMap: Record<any, React.FC<any>> = {
    [ServiceProviderType.Banking]: BankingAccount,
    [ServiceProviderType.BuildingSociety]: BankingAccount,
    [ServiceProviderType.CreditCards]: CreditCardsAccount,
    [ServiceProviderType.Email]: SocialMediaOrEmailAccount,
    [ServiceProviderType.Energy]: EnergyAccount,
    [ServiceProviderType.Insurance]: InsuranceAccount,
    [ServiceProviderType.Mobile]: MobileAccount,
    [ServiceProviderType.Mortgage]: MortgageAccount,
    [ServiceProviderType.Pension]: PensionAccount,
    [ServiceProviderType.SocialMedia]: SocialMediaOrEmailAccount,
    [ServiceProviderType.Water]: WaterAccount,
  };

  const SelectedAccountComponent =
    serviceProvider.customForm === "quilter"
      ? NEQuilterAccountSummary
      : accountComponentMap[serviceProviderType] || DefaultAccount;

  return (
    <Stack rowGap={2}>
      <SelectedAccountComponent
        account={account}
        persons={persons}
        documents={documents}
        properties={properties}
        serviceProvider={serviceProvider}
        serviceProviderType={serviceProvider.serviceProviderType}
      />
    </Stack>
  );
};

export interface AccountDisplayInfo {
  serviceProvider: ServiceProvider;
  serviceProviderType: ServiceProviderType;
  account: Account;
  persons: ReadonlyArray<Person>;
  documents: ReadonlyArray<Document>;
  properties: ReadonlyArray<Property>;
}
